/**
 * Shared component for displaying country details.
 * Used by both CountryCard and CountryInfoModal for DRY code.
 */

import type { CountryDisplayData } from '@/hooks/useCountryDisplayData';
import { parseJsonSafe } from '@/hooks/useCountryDisplayData';

interface CountryDetailsProps {
  /** Display data from useCountryDisplayData hook */
  data: CountryDisplayData;
  /** Whether to show in compact mode (fewer details) */
  compact?: boolean;
}

/**
 * Displays country information: description, famous for, details grid,
 * quality metrics (HDI/GDP), and positives/negatives.
 */
export function CountryDetails({ data, compact = false }: CountryDetailsProps) {
  const { country, description, famousFor, details, apiData } = data;

  if (!country) return null;

  return (
    <div className="space-y-4">
      {/* Description */}
      {description && (
        <p className={`text-sm text-muted-foreground leading-relaxed ${compact ? 'line-clamp-4' : ''}`}>
          {description}
        </p>
      )}

      {/* Famous for */}
      {famousFor && (
        <div className="text-sm">
          <span className="font-semibold">Famous for: </span>
          <span className="text-muted-foreground">{famousFor}</span>
        </div>
      )}

      {/* Official name if different */}
      {!compact && country.officialName && country.officialName !== country.name && (
        <div className="text-xs text-muted-foreground italic">
          Official: {country.officialName}
        </div>
      )}

      {/* Details grid */}
      {(details.capital || details.population) && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-3 border-t border-border/50">
          {details.capital && <InfoRow label="Capital" value={details.capital} />}
          {details.population && <InfoRow label="Population" value={details.population} />}
          {details.area && <InfoRow label="Area" value={details.area} />}
          {details.currency && <InfoRow label="Currency" value={details.currency} />}
          {details.languages && (
            <div className="col-span-2">
              <InfoRow label="Languages" value={details.languages} />
            </div>
          )}
        </div>
      )}

      {/* Quality metrics (HDI/GDP) */}
      {apiData && (apiData.hdi || apiData.gdp) && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-3 border-t border-border/50">
          {apiData.hdi && <InfoRow label="HDI" value={apiData.hdi} />}
          {apiData.gdp && <InfoRow label="GDP" value={apiData.gdp} />}
        </div>
      )}

      {/* Positives/Negatives */}
      {apiData && (apiData.positives || apiData.negatives) && (
        <div className="space-y-2 pt-3 border-t border-border/50">
          {apiData.positives && (
            <div>
              <div className="text-xs font-semibold text-green-600 mb-0.5">Positives</div>
              <div className="text-xs text-muted-foreground">
                {parseJsonSafe(apiData.positives).join(' | ')}
              </div>
            </div>
          )}
          {apiData.negatives && (
            <div>
              <div className="text-xs font-semibold text-red-600 mb-0.5">Negatives</div>
              <div className="text-xs text-muted-foreground">
                {parseJsonSafe(apiData.negatives).join(' | ')}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-sm">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}
